import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, resolveComponent as _resolveComponent, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, unref as _unref, isRef as _isRef, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { style: {"width":"100%","overflow":"auto"} }

import moment from "moment";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { computed, onMounted, ref, watch } from "vue";
import ReportType from "@/core/enums/ReportTypes.enums";
import { formatMonthQuery } from "@/core/helpers/bao-cao-pdf-params";
import { openBaoCaoPdf } from "@/components/shared/pdf-viewer/open-bao-cao-pdf";
import { swalErrNotification, downloadBase64File } from "@/core/helpers/utils";
import {
  setBreadcrumbLinks,
  setCurrentPageTitle,
} from "@/core/helpers/breadcrumb";
import { useCreatingForm } from "@/core/hooks/use-creating-form";
import { useDateTime } from "@/core/hooks/use-date-time-hook";
import { useLoading } from "@/core/hooks/use-loading";
import { usePagination } from "@/core/hooks/use-pagination";
import { KhoService } from "@/core/services/Kho.service";
import {
  IKhoResponseBase,
  ISoCanDoiVatTuTotalsResponseBase,
  IVatTuHangTonKhoItemV2ResponseBase,
} from "@/core/interfaces/ApiResponses";
import { useButtonCustom } from "@/core/hooks/use-button-custom";
import { BaoCaoService } from "@/core/services/BaoCao.service";
import ButtonCustom from "@/components/shared/buttons/ButtonCustom.vue";
import DivCol from "@/layout/_shared/DivCol.vue";
import DivRow from "@/layout/_shared/DivRow.vue";
import HangTonKhoTable from "./table-components/HangTonKhoTable.vue";
import HangTonKhoPrintModal from "@/views/crafted/pages/bao-cao/print-modals/HangTonKhoPrintModal.vue";
import LabelCustom from "@/components/shared/label-custom/LabelCustom.vue";
import PageContentBase from "@/layout/_shared/PageContentBase.vue";
import PageContentSection from "@/layout/_shared/PageContentSection.vue";
import Pagination from "@/components/shared/pagination/Pagination.vue";
import useFormInput from "@/core/hooks/use-form-input";


export default /*#__PURE__*/_defineComponent({
  __name: 'SoCanDoiVatTu',
  setup(__props) {

setCurrentPageTitle("Sổ cân đối hàng tồn kho");

setBreadcrumbLinks([
  {
    text: "Báo cáo",
  },
  {
    text: "Sổ cân đối hàng tồn kho",
  },
]);

document.title = "Sổ cân đối hàng tồn kho - Đường sắt Hà Nội";

const store = useStore();
const router = useRouter();

const currentYear = computed(() => store.getters.getSelectedYear);

const { disableDate, defaultDate } = useDateTime();

const { formRef } = useCreatingForm();

const { startLoading, endLoading } = useLoading();

const { currentPage, totalItems, searchTerm, perPage } = usePagination();

const { ButtonsType, ButtonTypeColors } = useButtonCustom();

const form = ref({
  from: defaultDate.value,
  to: defaultDate.value,
  khoId: -1,
  hideZero: true,
});

const isFiltersChanging = ref(true);

const khoItems = ref<IKhoResponseBase[]>([]);

const fromDateValidation = (_, value, callback) => {
  if (!value) {
    callback(new Error("Bắt buộc phải nhập"));
  }

  if (value && form.value.to && moment(value).isAfter(form.value.to)) {
    callback(new Error("Ngày bắt đầu phải trước ngày kết thúc"));
  }

  callback();
};

const toDateValidation = (_, value, callback) => {
  if (!value) {
    callback(new Error("Bắt buộc phải nhập"));
  }

  if (value && form.value.from && moment(value).isBefore(form.value.from)) {
    callback(new Error("Ngày bắt đầu phải trước ngày kết thúc"));
  }

  callback();
};

const rules = ref({
  from: [
    {
      validator: fromDateValidation,
      trigger: "change",
    },
  ],
  to: [
    {
      validator: toDateValidation,
      trigger: "change",
    },
  ],
  khoId: [
    {
      type: "number",
      message: "Phải chọn kho",
      trigger: "change",
      min: 1,
    },
  ],
});

const fetchKhoItems = async () => {
  startLoading();

  try {
    const {
      data: {
        data: { data },
      },
    } = await KhoService.fetchAllWarehouses();

    khoItems.value = data;

    await endLoading();
  } catch (e) {
    await endLoading();

    swalErrNotification(e, "Có lỗi khi lấy danh sách kho");
  }
};

onMounted(async () => {
  await fetchKhoItems();
});

const vatTuHangTonItems = ref<IVatTuHangTonKhoItemV2ResponseBase[]>([]);

const reportTotals = ref<ISoCanDoiVatTuTotalsResponseBase | null>(null);

const rowStartIndex = computed(() => (currentPage.value - 1) * perPage.value);

const { focusFirstErrInputV2 } = useFormInput();

const fetchBaoCao = async () => {
  startLoading();

  try {
    const {
      data: {
        data: { data: items, total, current_page: page },
        extra,
      },
    } = await BaoCaoService.getSoCanDoiVatTuV2({
      from: form.value.from.getMonth() + 1,
      to: form.value.to.getMonth() + 1,
      khoId: form.value.khoId,
      page: currentPage.value,
      perPage: perPage.value,
      search: searchTerm.value || undefined,
      hideZero: form.value.hideZero || undefined,
    });

    vatTuHangTonItems.value = items;
    totalItems.value = total;
    currentPage.value = page;
    reportTotals.value = extra?.totals ?? null;

    await endLoading();
  } catch (e) {
    await endLoading();

    swalErrNotification(e, "Có lỗi xảy ra");
  }
};

const generateBaoCao = () => {
  if (!formRef.value) return;

  formRef.value.validate(async (isValid) => {
    if (isValid) {
      currentPage.value = 1;
      await fetchBaoCao();
      isFiltersChanging.value = false;
    } else {
      await focusFirstErrInputV2();

      return false;
    }
  });
};

const exportExcel = () => {
  if (!formRef.value) return;

  formRef.value.validate(async (isValid) => {
    if (!isValid) {
      await focusFirstErrInputV2();

      return false;
    }

    startLoading();

    try {
      const {
        data: {
          data: { filename, file_data, mime_type },
        },
      } = await BaoCaoService.exportSoCanDoiVatTu({
        from: form.value.from.getMonth() + 1,
        to: form.value.to.getMonth() + 1,
        khoId: form.value.khoId,
        search: searchTerm.value || undefined,
        hideZero: form.value.hideZero || undefined,
      });

      await downloadBase64File(file_data, mime_type, filename);

      await endLoading();
    } catch (e) {
      await endLoading();

      swalErrNotification(e, "Có lỗi khi xuất Excel");
    }
  });
};

const onPageChange = async (page: number) => {
  currentPage.value = page;
  await fetchBaoCao();
};

const onPageSizeChange = async (pageSize: number) => {
  perPage.value = pageSize;
  currentPage.value = 1;
  await fetchBaoCao();
};

const hangTonKhoPrintModalRef = ref();

const openPdfViewer = () => {
  openBaoCaoPdf(router, {
    reportType: ReportType.SO_CAN_DOI_VAT_TU,
    from: formatMonthQuery(form.value.from),
    to: formatMonthQuery(form.value.to),
    khoId: form.value.khoId,
    hideZero: form.value.hideZero || undefined,
  });
};

const selectingKho = computed(() => {
  if (!form.value.khoId) return null;

  return khoItems.value.find(({ id }) => id === form.value.khoId);
});

const showPrintButton = computed(() => {
  if (isFiltersChanging.value) return false;

  if (!selectingKho.value) return false;

  return !moment(form.value.from).isAfter(form.value.to);
});

watch(
  form,
  () => {
    isFiltersChanging.value = true;
  },
  {
    deep: true,
    immediate: true,
  }
);

watch(searchTerm, () => {
  if (!isFiltersChanging.value) {
    isFiltersChanging.value = true;
  }
});

return (_ctx: any,_cache: any) => {
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_date_picker = _resolveComponent("el-date-picker")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_checkbox = _resolveComponent("el-checkbox")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_form = _resolveComponent("el-form")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(HangTonKhoPrintModal, {
      ref_key: "hangTonKhoPrintModalRef",
      ref: hangTonKhoPrintModalRef,
      from: new Date(form.value.from),
      items: vatTuHangTonItems.value,
      kho: selectingKho.value ?? null,
      to: new Date(form.value.to),
      onClose: _cache[0] || (_cache[0] = ($event: any) => (hangTonKhoPrintModalRef.value.isOpen = false))
    }, null, 8, ["from", "items", "kho", "to"]),
    _createVNode(PageContentBase, { plain: "" }, {
      default: _withCtx(() => [
        _createVNode(PageContentSection, { "always-expanded": true }, {
          header: _withCtx(() => [
            _createTextVNode(" Sổ cân đối hàng tồn kho ")
          ]),
          default: _withCtx(() => [
            _createVNode(_component_el_form, {
              ref_key: "formRef",
              ref: formRef,
              model: form.value,
              rules: rules.value,
              onSubmit: _withModifiers(generateBaoCao, ["prevent"])
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_row, {
                  gutter: 10,
                  align: "bottom",
                  class: "mb-4"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_col, {
                      lg: 5,
                      md: 8,
                      sm: 12,
                      xl: 4,
                      xs: 24
                    }, {
                      default: _withCtx(() => [
                        _createVNode(LabelCustom, {
                          "is-text-muted": false,
                          "is-required": true
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode("Kho:")
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_form_item, {
                          prop: "khoId",
                          class: "mb-0"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_select, {
                              modelValue: form.value.khoId,
                              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.value.khoId) = $event)),
                              class: "w-100",
                              name: "khoId"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_el_option, {
                                  label: "Chưa chọn",
                                  value: -1
                                }),
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(khoItems.value, (kho) => {
                                  return (_openBlock(), _createBlock(_component_el_option, {
                                    key: kho.id,
                                    label: kho.ten_kho,
                                    value: kho.id
                                  }, null, 8, ["label", "value"]))
                                }), 128))
                              ]),
                              _: 1
                            }, 8, ["modelValue"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_col, {
                      lg: 3,
                      md: 8,
                      sm: 12,
                      xl: 3,
                      xs: 24
                    }, {
                      default: _withCtx(() => [
                        _createVNode(LabelCustom, {
                          "is-text-muted": false,
                          "is-required": true
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode("Từ tháng:")
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_form_item, {
                          prop: "from",
                          class: "mb-0"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_date_picker, {
                              modelValue: form.value.from,
                              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.value.from) = $event)),
                              class: "w-100",
                              "disabled-date": (time) => _unref(disableDate)(time, currentYear.value, true),
                              type: "month",
                              placeholder: "Từ",
                              name: "from",
                              format: "MM/YYYY"
                            }, null, 8, ["modelValue", "disabled-date"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_col, {
                      lg: 3,
                      md: 8,
                      sm: 12,
                      xl: 3,
                      xs: 24
                    }, {
                      default: _withCtx(() => [
                        _createVNode(LabelCustom, {
                          "is-text-muted": false,
                          "is-required": true
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode("Đến tháng:")
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_form_item, {
                          prop: "to",
                          class: "mb-0"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_date_picker, {
                              modelValue: form.value.to,
                              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.value.to) = $event)),
                              class: "w-100",
                              "disabled-date": (time) => _unref(disableDate)(time, currentYear.value, true),
                              placeholder: "Đến",
                              name: "to",
                              format: "MM/YYYY",
                              type: "month"
                            }, null, 8, ["modelValue", "disabled-date"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_col, {
                      lg: 7,
                      md: 8,
                      sm: 12,
                      xl: 7,
                      xs: 24
                    }, {
                      default: _withCtx(() => [
                        _createVNode(LabelCustom, { "is-text-muted": false }, {
                          default: _withCtx(() => [
                            _createTextVNode("Tìm kiếm:")
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_form_item, { class: "mb-0" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_input, {
                              modelValue: _unref(searchTerm),
                              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (_isRef(searchTerm) ? (searchTerm).value = $event : null)),
                              class: "w-100",
                              placeholder: "Mã hoặc tên vật tư",
                              clearable: ""
                            }, null, 8, ["modelValue"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_col, {
                      lg: 6,
                      md: 8,
                      sm: 12,
                      xl: 7,
                      xs: 24
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_form_item, { class: "mb-0" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_checkbox, {
                              modelValue: form.value.hideZero,
                              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((form.value.hideZero) = $event))
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(" Chỉ lấy vật tư có dữ liệu ")
                              ]),
                              _: 1
                            }, 8, ["modelValue"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(DivRow, { class: "mb-4" }, {
                  default: _withCtx(() => [
                    _createVNode(DivCol, { cols: 6 }),
                    _createVNode(DivCol, {
                      cols: 6,
                      class: "d-flex justify-content-end"
                    }, {
                      default: _withCtx(() => [
                        (showPrintButton.value)
                          ? (_openBlock(), _createBlock(ButtonCustom, {
                              key: 0,
                              "button-type": _unref(ButtonsType).PRINT,
                              onClick: openPdfViewer
                            }, {
                              "button-label": _withCtx(() => [
                                _createTextVNode("In phiếu")
                              ]),
                              _: 1
                            }, 8, ["button-type"]))
                          : _createCommentVNode("", true),
                        (showPrintButton.value)
                          ? (_openBlock(), _createBlock(ButtonCustom, {
                              key: 1,
                              "button-type": _unref(ButtonsType).CLOUD_DOWNLOAD,
                              "color-type": _unref(ButtonTypeColors).WARNING,
                              "tooltip-message": "Xuất Excel",
                              onClick: exportExcel
                            }, {
                              "button-label": _withCtx(() => [
                                _createTextVNode("Xuất Excel")
                              ]),
                              _: 1
                            }, 8, ["button-type", "color-type"]))
                          : _createCommentVNode("", true),
                        _createVNode(ButtonCustom, {
                          "button-type": _unref(ButtonsType).FA_FILE_EXPORT_THIN,
                          "color-type": _unref(ButtonTypeColors).SUCCESS,
                          "tooltip-message": "Xuất báo cáo",
                          onClick: generateBaoCao
                        }, {
                          "button-label": _withCtx(() => [
                            _createTextVNode("Xuất báo cáo")
                          ]),
                          _: 1
                        }, 8, ["button-type", "color-type"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["model", "rules"]),
            _createElementVNode("div", _hoisted_1, [
              (!isFiltersChanging.value)
                ? (_openBlock(), _createBlock(HangTonKhoTable, {
                    key: 0,
                    "is-print-modal": false,
                    items: vatTuHangTonItems.value,
                    "row-start-index": rowStartIndex.value,
                    stripe: true,
                    totals: reportTotals.value
                  }, null, 8, ["items", "row-start-index", "totals"]))
                : _createCommentVNode("", true)
            ]),
            (!isFiltersChanging.value && _unref(totalItems) > 0)
              ? (_openBlock(), _createBlock(Pagination, {
                  key: 0,
                  page: _unref(currentPage),
                  "page-size": _unref(perPage),
                  total: _unref(totalItems),
                  onChangePage: onPageChange,
                  onChangePageSize: onPageSizeChange
                }, null, 8, ["page", "page-size", "total"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ], 64))
}
}

})